// =============================================================================
// Saldos de Arquitetos — Portal Arquitetos (admin, somente leitura)
// =============================================================================
// Resposta rápida pra pergunta: "quanto exatamente cada arquiteto ainda tem
// de saldo disponível para saque, agora mesmo?"
//
// Fonte: GET /comissoes/saldos-arquitetos — já aplica a regra de liberação
// (DIAS_LIBERA_SAQUE, situação do ERP, não devolução/pago) e desconta o que
// está reservado em solicitações pendentes/aprovadas.
//
// Permissão: `saldos_arquitetos`.
// =============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const brl = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataFmt = d => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  const [y, m, day] = s.split('T')[0].split('-');
  return `${day}/${m}/${y}`;
};

const normalizarBusca = v => String(v ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim();

const contemBusca = (campos, busca) => {
  const termo = normalizarBusca(busca);
  if (!termo) return true;
  return campos.some(c => normalizarBusca(c).includes(termo));
};

function SortTh({ col, sort, onSort, children, className, style }) {
  const ativo = sort.col === col;
  const icone = ativo
    ? (sort.dir === 'asc' ? 'bi-sort-up' : 'bi-sort-down')
    : 'bi-arrow-down-up';
  return (
    <th
      className={className}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
      onClick={() => onSort(col)}
    >
      {children}{' '}
      <i className={`bi ${icone}`} style={{ opacity: ativo ? 1 : 0.3, fontSize: '0.72rem' }} />
    </th>
  );
}

const toggleSort = (setter, col) =>
  setter(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));

const dateToNum = v => {
  if (v == null) return null;
  const t = new Date(v).getTime();
  return isNaN(t) ? null : t;
};

const aplicarSort = (lista, sort, getVal) => {
  if (!sort.col) return lista;
  return [...lista].sort((a, b) => {
    const va = getVal(a, sort.col);
    const vb = getVal(b, sort.col);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const r = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), 'pt-BR', { numeric: true });
    return sort.dir === 'asc' ? r : -r;
  });
};

export default function SaldosArquitetos() {
  const [saldos, setSaldos]       = useState([]);
  const [carregando, setCarreg]   = useState(true);
  const [erro, setErro]           = useState('');
  const [busca, setBusca]         = useState('');
  const [somenteVinculados, setSomenteVinculados] = useState(true);
  const [sort, setSort]           = useState({ col: 'saldo_disponivel', dir: 'desc' });

  const carregar = useCallback(() => {
    setCarreg(true);
    setErro('');
    api.get('/comissoes/saldos-arquitetos')
      .then(r => setSaldos(r.data))
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar saldos'))
      .finally(() => setCarreg(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const listaFiltrada = useMemo(() => {
    const filtrada = saldos
      .filter(r => somenteVinculados ? r.vinculado : true)
      .filter(r => contemBusca([
        r.nome,
        r.filial,
        r.email,
        brl(r.saldo_disponivel),
        brl(r.saldo_bruto),
        brl(r.valor_estornos),
        brl(r.valor_reservado),
        brl(r.saldo_total),
        r.qtd_comissoes,
      ], busca));
    return aplicarSort(filtrada, sort, (r, col) => {
      if (col === 'comissao_mais_antiga' || col === 'comissao_mais_recente') return dateToNum(r[col]);
      return r[col];
    });
  }, [saldos, busca, somenteVinculados, sort]);

  const totais = useMemo(() => listaFiltrada.reduce((acc, r) => ({
    saldo_disponivel: acc.saldo_disponivel + (r.saldo_disponivel || 0),
    saldo_bruto:      acc.saldo_bruto      + (r.saldo_bruto      || 0),
    valor_estornos:   acc.valor_estornos   + (r.valor_estornos   || 0),
    valor_reservado:  acc.valor_reservado  + (r.valor_reservado  || 0),
    saldo_total:      acc.saldo_total      + (r.saldo_total      || 0),
    qtd_comissoes:    acc.qtd_comissoes    + (r.qtd_comissoes    || 0),
  }), { saldo_disponivel: 0, saldo_bruto: 0, valor_estornos: 0, valor_reservado: 0, saldo_total: 0, qtd_comissoes: 0 }), [listaFiltrada]);

  const nVinculados    = saldos.filter(r => r.vinculado).length;
  const nNaoVinculados = saldos.length - nVinculados;

  return (
    <div>
      <h4 className="fw-bold mb-1">
        <i className="bi bi-wallet2 me-2" style={{ color: 'var(--primary)' }}></i>
        Saldos de Arquitetos
      </h4>
      <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>
        Quanto cada arquiteto ainda tem de saldo disponível para saque — já descontado o que está reservado em solicitações pendentes/aprovadas.
      </p>

      {/* Cards de totais */}
      {!carregando && !erro && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-sm-3">
            <div className="card card-stat p-3" style={{ borderLeftColor: 'var(--primary)' }}>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Disponível para saque
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary)', marginTop: 4 }}>
                {brl(totais.saldo_disponivel)}
              </div>
            </div>
          </div>
          <div className="col-6 col-sm-3">
            <div className="card card-stat p-3" style={{ borderLeftColor: 'var(--accent)' }}>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Reservado em solicitações
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--accent)', marginTop: 4 }}>
                {brl(totais.valor_reservado)}
              </div>
            </div>
          </div>
          <div className="col-6 col-sm-3">
            <div className="card card-stat p-3" style={{ borderLeftColor: '#888' }}>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Total a pagar
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#444', marginTop: 4 }}>
                {brl(totais.saldo_total)}
              </div>
            </div>
          </div>
          <div className="col-6 col-sm-3">
            <div className="card card-stat p-3" style={{ borderLeftColor: 'var(--success)' }}>
              <div style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Arquitetos com saldo
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--success)', marginTop: 4 }}>
                {listaFiltrada.length}
                {!somenteVinculados && nNaoVinculados > 0 && (
                  <span className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 500, marginLeft: 6 }}>
                    ({nVinculados} vinculados)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="card p-0">
        <div className="p-3 border-bottom bg-white d-flex flex-wrap gap-3 align-items-center">
          <div className="input-group" style={{ maxWidth: 420 }}>
            <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
            <input
              type="search"
              className="form-control"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Filtrar por nome, filial, e-mail ou valor"
            />
            {busca && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => setBusca('')} title="Limpar">
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>

          <div className="form-check form-switch ms-auto">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="chk-somente-vinculados"
              checked={somenteVinculados}
              onChange={e => setSomenteVinculados(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="chk-somente-vinculados" style={{ fontSize: '0.88rem' }}>
              Mostrar só arquitetos vinculados
              {!somenteVinculados && nNaoVinculados > 0 && (
                <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>
                  (exibindo {nNaoVinculados} não vinculado{nNaoVinculados > 1 ? 's' : ''})
                </span>
              )}
            </label>
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={carregar}
            disabled={carregando}
            title="Recarregar"
          >
            <i className={`bi ${carregando ? 'bi-arrow-clockwise' : 'bi-arrow-repeat'} me-1`}></i>
            Atualizar
          </button>
        </div>

        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
          </div>
        ) : erro ? (
          <div className="alert alert-danger m-3">{erro}</div>
        ) : (
          <div className="tabela-container">
            <table className="table table-hover table-arq mb-0">
              <thead>
                <tr>
                  <SortTh col="nome" sort={sort} onSort={c => toggleSort(setSort, c)}>Arquiteto</SortTh>
                  <SortTh col="filial" sort={sort} onSort={c => toggleSort(setSort, c)}>Filial</SortTh>
                  <SortTh col="saldo_bruto" sort={sort} onSort={c => toggleSort(setSort, c)} className="text-end">
                    Bruto
                  </SortTh>
                  <SortTh col="valor_estornos" sort={sort} onSort={c => toggleSort(setSort, c)} className="text-end">
                    Estornos abertos
                  </SortTh>
                  <SortTh col="saldo_disponivel" sort={sort} onSort={c => toggleSort(setSort, c)} className="text-end">
                    Disponível p/ saque
                  </SortTh>
                  <SortTh col="valor_reservado" sort={sort} onSort={c => toggleSort(setSort, c)} className="text-end">
                    Reservado
                  </SortTh>
                  <SortTh col="qtd_comissoes" sort={sort} onSort={c => toggleSort(setSort, c)} className="text-center">
                    Comissões
                  </SortTh>
                  <SortTh col="comissao_mais_antiga" sort={sort} onSort={c => toggleSort(setSort, c)}>
                    Mais antiga
                  </SortTh>
                </tr>
              </thead>
              <tbody>
                {listaFiltrada.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted py-4">Nenhum arquiteto com saldo encontrado</td></tr>
                ) : listaFiltrada.map(r => (
                  <tr key={r.oid}>
                    <td className="fw-semibold">
                      {r.nome}
                      {!r.vinculado && (
                        <span className="badge-neutro ms-2" title="Arquiteto ainda não vinculado ao portal">
                          Não vinculado
                        </span>
                      )}
                      {r.vinculado && r.vinculo_ativo === false && (
                        <span className="badge-pendente ms-2" title="Vínculo inativo no portal">
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.88rem' }}>{r.filial || '—'}</td>
                    <td className="text-end text-muted">{brl(r.saldo_bruto)}</td>
                    <td
                      className="text-end"
                      style={{ color: r.valor_estornos < 0 ? 'var(--danger, #dc3545)' : '#999' }}
                      title={r.qtd_estornos > 0 ? `${r.qtd_estornos} CREIND-DEV/CAN em aberto` : ''}
                    >
                      {r.valor_estornos < 0 ? brl(r.valor_estornos) : '—'}
                    </td>
                    <td className="text-end fw-semibold" style={{ color: 'var(--primary)' }}>
                      {brl(r.saldo_disponivel)}
                    </td>
                    <td className="text-end" style={{ color: r.valor_reservado > 0 ? 'var(--accent)' : '#999' }}>
                      {r.valor_reservado > 0 ? brl(r.valor_reservado) : '—'}
                    </td>
                    <td className="text-center text-muted">{r.qtd_comissoes}</td>
                    <td className="text-muted" style={{ fontSize: '0.88rem', whiteSpace: 'nowrap' }}>
                      {dataFmt(r.comissao_mais_antiga)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {listaFiltrada.length > 1 && (
                <tfoot>
                  <tr style={{ background: '#f8f9fa', borderTop: '2px solid #dee2e6', fontWeight: 700 }}>
                    <td colSpan={2}>Total</td>
                    <td className="text-end text-muted">{brl(totais.saldo_bruto)}</td>
                    <td className="text-end" style={{ color: totais.valor_estornos < 0 ? 'var(--danger, #dc3545)' : '#999' }}>
                      {totais.valor_estornos < 0 ? brl(totais.valor_estornos) : '—'}
                    </td>
                    <td className="text-end" style={{ color: 'var(--primary)' }}>{brl(totais.saldo_disponivel)}</td>
                    <td className="text-end" style={{ color: 'var(--accent)' }}>{brl(totais.valor_reservado)}</td>
                    <td className="text-center text-muted">{totais.qtd_comissoes}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
